import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { Badge } from '../../components/ui/Badge.jsx';
import { MetricCard } from '../../components/ui/Card.jsx';
import { Table } from '../../components/ui/Table.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { productionApi } from '../../services/productionApi.js';

const HORIZONS = [7, 30, 90];

function UtilBar({ pct }) {
  if (pct == null) return <span className="muted">-</span>;
  const clamped = Math.min(pct, 100);
  const color = pct > 100 ? 'var(--color-danger, #dc2626)' : pct >= 85 ? 'var(--color-warning, #d97706)' : 'var(--color-success, #059669)';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
      <div style={{ flex: 1, minWidth: 80, height: 8, background: 'var(--color-border)', borderRadius: 4, overflow: 'hidden' }}>
        <div style={{ width: `${clamped}%`, height: '100%', background: color }} />
      </div>
      <span className="mono num text-sm" style={{ color, minWidth: 48, textAlign: 'right' }}>{pct}%</span>
    </div>
  );
}

export function CapacityPlanningPage() {
  const { t } = useTranslation();
  const [horizon, setHorizon] = useState(30);

  const report = useQuery({
    queryKey: ['production', 'capacity', horizon],
    queryFn: () => productionApi.capacity({ horizonDays: horizon }).then((r) => r.items),
  });
  const rows = report.data?.workCenters || [];
  const totals = report.data?.totals;

  const columns = [
    { key: 'code', label: t('cap.col.code'), render: (r) => <span className="mono"><strong>{r.code}</strong></span> },
    { key: 'name', label: t('cap.col.name') },
    { key: 'capacity', label: t('cap.col.perDay'), align: 'right', render: (r) => <span className="mono num">{Number(r.capacityPerDay).toFixed(1)}</span> },
    { key: 'available', label: t('cap.col.available'), align: 'right', render: (r) => <span className="mono num">{Number(r.availableHours).toFixed(0)} h</span> },
    { key: 'load', label: t('cap.col.load'), align: 'right', render: (r) => <span className="mono num">{Number(r.loadHours).toFixed(0)} h <span className="muted text-sm">({r.openOrders})</span></span> },
    { key: 'util', label: t('cap.col.utilization'), render: (r) => <UtilBar pct={r.utilizationPct} /> },
    { key: 'state', label: '', render: (r) => (r.overloaded ? <Badge variant="danger">{t('cap.overloaded')}</Badge> : null) },
  ];

  return (
    <section className="page">
      <header className="page-header">
        <div>
          <h1>{t('cap.title')}</h1>
          <p className="muted">{t('cap.subtitle')}</p>
        </div>
        <div className="page-actions">
          <select className="input" style={{ maxWidth: 170 }} value={horizon} onChange={(e) => setHorizon(Number(e.target.value))}>
            {HORIZONS.map((h) => <option key={h} value={h}>{t('cap.horizon', { n: h })}</option>)}
          </select>
        </div>
      </header>

      <div className="card-grid">
        <MetricCard label={t('cap.metric.available')} value={`${Number(totals?.availableHours || 0).toFixed(0)} h`} />
        <MetricCard label={t('cap.metric.load')} value={`${Number(totals?.loadHours || 0).toFixed(0)} h`} />
        <MetricCard label={t('cap.metric.utilization')} value={totals?.utilizationPct != null ? `${totals.utilizationPct} %` : '-'}
          trendVariant={Number(totals?.utilizationPct) > 100 ? 'danger' : 'success'} />
        <MetricCard label={t('cap.metric.overloaded')} value={String(rows.filter((r) => r.overloaded).length)} trendVariant={rows.some((r) => r.overloaded) ? 'danger' : 'success'} />
      </div>

      <Table columns={columns} rows={rows} isLoading={report.isLoading} emptyMessage={t('cap.empty')} />
    </section>
  );
}
